import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  Animation as AnimationIcon,
  Edit as EditIcon,
  DragIndicator as DragIcon,
  PlayArrow as PlayIcon,
  Code as CodeIcon,
  Fullscreen as FullscreenIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-toastify';
import animationService, { Animation } from '../../services/animationService';
import animationContainerService, { AnimationContainer } from '../../services/animationContainerService';
import { registerBeforeTabChange, unregisterBeforeTabChange } from './Workshop';
import ParentDropZone from '../../components/ParentDropZone';

// Sortable container component
const SortableContainer: React.FC<{
  container: any;
  onNavigate: (id: number) => void;
  onEdit: (container: any) => void;
  onDelete: (id: number) => void;
  activeItemType: 'container' | 'animation' | null;
  isNestTarget: boolean;
  showDescription?: boolean;
}> = ({ container, onNavigate, onEdit, onDelete, activeItemType, isNestTarget, showDescription }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `container-${container.id}` });

  const style = {
    transform: (!isDragging && activeItemType !== null) ? undefined : CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        cursor: 'pointer',
        border: '2px dashed',
        borderColor: isNestTarget ? 'primary.main' : 'primary.light',
        backgroundColor: isNestTarget ? 'primary.light' : isDragging ? 'action.hover' : 'inherit',
        boxShadow: isNestTarget ? '0 4px 12px rgba(25, 118, 210, 0.25)' : 'none',
      }}
    >
      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', '&:active': { cursor: 'grabbing' } }}
          >
            <DragIcon fontSize="small" color="action" />
          </Box>
          <FolderIcon color="primary" />
          <Typography
            variant="body2"
            sx={{ flex: 1, cursor: 'pointer' }}
            onClick={() => onNavigate(container.id)}
          >
            {container.title}
          </Typography>
          <IconButton size="small" onClick={() => onEdit(container)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(container.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
        {showDescription && container.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 5 }}>
            {container.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Sortable animation item component
const SortableAnimation: React.FC<{
  animation: any;
  isSelected: boolean;
  onSelect: (animation: any) => void;
  onDuplicate: (id: number) => void;
  onDelete: (id: number) => void;
}> = ({ animation, isSelected, onSelect, onDuplicate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `animation-${animation.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        cursor: 'pointer',
        backgroundColor: isSelected ? 'primary.light' : 'inherit',
        borderLeft: isSelected ? '4px solid' : 'none',
        borderLeftColor: isSelected ? 'primary.main' : undefined,
        '&:hover': { backgroundColor: isSelected ? 'primary.light' : 'action.hover' },
      }}
      onClick={() => onSelect(animation)}
    >
      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', '&:active': { cursor: 'grabbing' } }}
            onClick={(e) => e.stopPropagation()}
          >
            <DragIcon fontSize="small" color="action" />
          </Box>
          <FileIcon fontSize="small" color="action" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {animation.title}
            </Typography>
          </Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDuplicate(animation.id); }}>
            <DuplicateIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(animation.id); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

// Draft cache types
interface AnimationDraft {
  serverSnapshot: any;
  title: string;
  htmlContent: string;
}

interface AnimationsProps {
  urlContainerId?: number | null;
  urlItemId?: number | null;
  onNavChange?: (containerId: number | null, itemId: number | null, label?: string) => void;
}

const Animations: React.FC<AnimationsProps> = ({ urlContainerId, urlItemId, onNavChange }) => {
  const [animations, setAnimations] = useState<any[]>([]);
  const [containers, setContainers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentContainer, setCurrentContainer] = useState<number | null>(null);
  const [parentContainer, setParentContainer] = useState<number | null>(null);

  // Draft cache
  const [activeAnimationId, setActiveAnimationId] = useState<number | null>(null);
  const [draftCache, setDraftCache] = useState<Map<number, AnimationDraft>>(new Map());
  const draftCacheRef = useRef<Map<number, AnimationDraft>>(new Map());
  const activeAnimationIdRef = useRef<number | null>(null);

  useEffect(() => { draftCacheRef.current = draftCache; }, [draftCache]);
  useEffect(() => { activeAnimationIdRef.current = activeAnimationId; }, [activeAnimationId]);

  // Sync from URL (back/forward navigation)
  useEffect(() => {
    if (urlContainerId !== undefined && urlContainerId !== currentContainer) {
      setCurrentContainer(urlContainerId);
    }
  }, [urlContainerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (urlItemId !== undefined && urlItemId !== activeAnimationId) {
      setActiveAnimationId(urlItemId);
    }
  }, [urlItemId]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeDraft = activeAnimationId !== null ? draftCache.get(activeAnimationId) : undefined;
  const selectedAnimation = activeDraft?.serverSnapshot ?? null;
  const title = activeDraft?.title ?? '';
  const htmlContent = activeDraft?.htmlContent ?? '';

  const updateActiveDraft = useCallback(<K extends keyof AnimationDraft>(field: K, value: AnimationDraft[K]) => {
    setDraftCache(prev => {
      const id = activeAnimationIdRef.current;
      if (id === null) return prev;
      const draft = prev.get(id);
      if (!draft) return prev;
      const next = new Map(prev);
      next.set(id, { ...draft, [field]: value });
      return next;
    });
  }, []);

  const setTitle = useCallback((v: string) => updateActiveDraft('title', v), [updateActiveDraft]);
  const setHtmlContent = useCallback((v: string) => updateActiveDraft('htmlContent', v), [updateActiveDraft]);

  // DnD
  const [activeItemType, setActiveItemType] = useState<'container' | 'animation' | null>(null);
  const [nestTargetId, setNestTargetId] = useState<number | null>(null);

  // Dialogs
  const [animationDialogOpen, setAnimationDialogOpen] = useState(false);
  const [createType, setCreateType] = useState<'container' | 'animation'>('animation');
  const [newContainerTitle, setNewContainerTitle] = useState('');
  const [newContainerDescription, setNewContainerDescription] = useState('');
  const [newAnimationTitle, setNewAnimationTitle] = useState('');
  const [showDescriptions, setShowDescriptions] = useState(false);

  // Edit container dialog
  const [editContainerDialogOpen, setEditContainerDialogOpen] = useState(false);
  const [editingContainer, setEditingContainer] = useState<any | null>(null);
  const [editContainerTitle, setEditContainerTitle] = useState('');
  const [editContainerDescription, setEditContainerDescription] = useState('');

  // Preview
  const [showPreview, setShowPreview] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // Auto-save
  const saveDraftIfDirty = useCallback(async (animId: number | null) => {
    if (animId === null) return;
    const draft = draftCacheRef.current.get(animId);
    if (!draft) return;
    const snap = draft.serverSnapshot;
    const dirty = draft.title !== (snap.title || '') || draft.htmlContent !== (snap.html_content || '');
    if (!dirty) return;
    try {
      const updated = await animationService.update(animId, {
        title: draft.title,
        html_content: draft.htmlContent,
      });
      setDraftCache(prev => {
        const next = new Map(prev);
        const existing = next.get(animId);
        if (existing) {
          next.set(animId, { ...existing, serverSnapshot: updated });
        }
        return next;
      });
    } catch (error) {
      console.error('Error auto-saving animation:', error);
    }
  }, []);

  useEffect(() => {
    registerBeforeTabChange('animations', async () => {
      await saveDraftIfDirty(activeAnimationIdRef.current);
    });
    return () => unregisterBeforeTabChange('animations');
  }, [saveDraftIfDirty]);

  // Auto-save on blur
  useEffect(() => {
    const timer = setInterval(() => {
      saveDraftIfDirty(activeAnimationIdRef.current);
    }, 10000);
    return () => clearInterval(timer);
  }, [saveDraftIfDirty]);

  // Load data
  const loadContainers = useCallback(async () => {
    try {
      const data = await animationContainerService.getContainers(currentContainer);
      setContainers(data);
    } catch (error) {
      console.error('Error loading containers:', error);
    }
  }, [currentContainer]);

  const loadAnimations = useCallback(async () => {
    try {
      const params: any = {};
      if (currentContainer) params.container = currentContainer;
      else params.container = 'null';
      const data = await animationService.getAll(params);
      setAnimations(data);
    } catch (error) {
      console.error('Error loading animations:', error);
    }
  }, [currentContainer]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadContainers(), loadAnimations()]).finally(() => setLoading(false));
  }, [loadContainers, loadAnimations]);

  // Navigate containers
  const handleNavigateToContainer = async (containerId: number) => {
    await saveDraftIfDirty(activeAnimationIdRef.current);
    try {
      const container = await animationContainerService.getContainer(containerId);
      setParentContainer(container.parent);
    } catch { /* ignore */ }
    setCurrentContainer(containerId);
    setActiveAnimationId(null);
    const cont = containers.find(c => c.id === containerId);
    onNavChange?.(containerId, null, cont?.title);
  };

  const handleGoToParent = async () => {
    await saveDraftIfDirty(activeAnimationIdRef.current);
    if (parentContainer !== null) {
      try {
        const container = await animationContainerService.getContainer(parentContainer);
        setCurrentContainer(parentContainer);
        setParentContainer(container.parent);
        onNavChange?.(parentContainer, null, container.title);
      } catch {
        setCurrentContainer(null);
        setParentContainer(null);
        onNavChange?.(null, null);
      }
    } else {
      setCurrentContainer(null);
      setParentContainer(null);
      onNavChange?.(null, null);
    }
    setActiveAnimationId(null);
  };

  const handleSelectAnimation = (animation: any) => {
    saveDraftIfDirty(activeAnimationIdRef.current);
    const newId = animation.id;
    setActiveAnimationId(newId);
    onNavChange?.(currentContainer, newId, animation.title);
    if (!draftCache.has(newId)) {
      setDraftCache(prev => {
        if (prev.has(newId)) return prev;
        const next = new Map(prev);
        next.set(newId, {
          serverSnapshot: animation,
          title: animation.title || '',
          htmlContent: animation.html_content || '',
        });
        return next;
      });
    }
  };

  // CRUD
  const handleNew = () => {
    setAnimationDialogOpen(true);
    setCreateType('animation');
  };

  const handleCreateContainer = async () => {
    if (!newContainerTitle.trim()) {
      toast.error('Le nom du contenant est requis');
      return;
    }
    try {
      await animationContainerService.createContainer({
        title: newContainerTitle,
        description: newContainerDescription,
        parent: currentContainer,
      });
      toast.success('Contenant créé');
      setAnimationDialogOpen(false);
      setNewContainerTitle('');
      setNewContainerDescription('');
      loadContainers();
    } catch (error) {
      toast.error('Erreur lors de la création du contenant');
    }
  };

  const handleCreateNewAnimation = async () => {
    if (!newAnimationTitle.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    try {
      const newAnim = await animationService.create({
        title: newAnimationTitle,
        html_content: '',
        is_public: false,
        container: currentContainer,
        tags: [],
      });
      toast.success('Animation créée');
      setAnimationDialogOpen(false);
      setNewAnimationTitle('');
      setDraftCache(prev => {
        const next = new Map(prev);
        next.set(newAnim.id, {
          serverSnapshot: newAnim,
          title: newAnim.title || '',
          htmlContent: newAnim.html_content || '',
        });
        return next;
      });
      setActiveAnimationId(newAnim.id);
      loadAnimations();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleEditContainer = (container: any) => {
    setEditingContainer(container);
    setEditContainerTitle(container.title);
    setEditContainerDescription(container.description || '');
    setEditContainerDialogOpen(true);
  };

  const handleUpdateContainer = async () => {
    if (!editContainerTitle.trim()) {
      toast.error('Le nom du contenant est requis');
      return;
    }
    try {
      await animationContainerService.updateContainer(editingContainer.id, {
        title: editContainerTitle,
        description: editContainerDescription,
      });
      toast.success('Contenant mis à jour');
      setEditContainerDialogOpen(false);
      setEditingContainer(null);
      setEditContainerTitle('');
      setEditContainerDescription('');
      loadContainers();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du contenant');
    }
  };

  const handleDeleteContainer = async (containerId: number) => {
    if (!window.confirm('Supprimer ce contenant et tout son contenu ?')) return;
    try {
      await animationContainerService.deleteContainer(containerId);
      toast.success('Contenant supprimé');
      loadContainers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDuplicate = async (animationId: number) => {
    try {
      await animationService.duplicate(animationId);
      toast.success('Animation dupliquée');
      loadAnimations();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleDelete = async (animationId: number) => {
    if (!window.confirm('Supprimer cette animation ?')) return;
    try {
      await animationService.delete(animationId);
      toast.success('Animation supprimée');
      if (activeAnimationId === animationId) {
        setActiveAnimationId(null);
      }
      loadAnimations();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSave = async () => {
    if (!selectedAnimation) return;
    try {
      const updated = await animationService.update(selectedAnimation.id, {
        title,
        html_content: htmlContent,
      });
      setDraftCache(prev => {
        const next = new Map(prev);
        const existing = next.get(selectedAnimation.id);
        if (existing) {
          next.set(selectedAnimation.id, { ...existing, serverSnapshot: updated });
        }
        return next;
      });
      toast.success('Animation sauvegardée');
      loadAnimations();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Fullscreen preview
  const handleFullscreen = () => {
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(htmlContent);
      win.document.close();
    }
  };

  // DnD handlers
  const isContainerItem = (item: any) => containers.some(c => c.id === item.id);
  const handleDragStart = (event: DragStartEvent) => {
    const idStr = String(event.active.id);
    setActiveItemType(idStr.startsWith('container-') ? 'container' : 'animation');
  };
  const handleDragOver = (event: DragOverEvent) => {
    if (activeItemType !== 'animation') { setNestTargetId(null); return; }
    const overId = event.over?.id ? String(event.over.id) : null;
    if (overId && overId.startsWith('container-')) {
      setNestTargetId(parseInt(overId.replace('container-', '')));
    } else {
      setNestTargetId(null);
    }
  };
  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveItemType(null);
    setNestTargetId(null);
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);

    // Animation dropped on container → move to container
    if (activeId.startsWith('animation-') && overId.startsWith('container-')) {
      const animId = parseInt(activeId.replace('animation-', ''));
      const containerId = parseInt(overId.replace('container-', ''));
      try {
        await animationService.update(animId, { container: containerId } as any);
        loadAnimations();
      } catch { /* ignore */ }
      return;
    }

    if (activeId === overId) return;

    // Reorder containers
    if (activeId.startsWith('container-') && overId.startsWith('container-')) {
      const oldIndex = containers.findIndex(c => `container-${c.id}` === activeId);
      const newIndex = containers.findIndex(c => `container-${c.id}` === overId);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(containers, oldIndex, newIndex);
        setContainers(newOrder);
        const orderData = newOrder.map((item, idx) => ({ id: item.id, order: idx }));
        await animationContainerService.reorderContainers(orderData);
      }
      return;
    }

    // Reorder animations
    if (activeId.startsWith('animation-') && overId.startsWith('animation-')) {
      const allItems = [...containers.map(c => ({ ...c, _type: 'container' })), ...animations.map(a => ({ ...a, _type: 'animation' }))];
      const oldIndex = allItems.findIndex(i => (i._type === 'animation' ? `animation-${i.id}` : `container-${i.id}`) === activeId);
      const newIndex = allItems.findIndex(i => (i._type === 'animation' ? `animation-${i.id}` : `container-${i.id}`) === overId);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(allItems, oldIndex, newIndex);
        const newAnims = newItems.filter(i => i._type === 'animation');
        setAnimations(newAnims);
        const orderData = newAnims.map((item, idx) => ({ id: item.id, order: idx }));
        await animationService.reorder(orderData);
      }
    }
  };

  const sortableItems = [
    ...containers.map(c => `container-${c.id}`),
    ...animations.map(a => `animation-${a.id}`),
  ];

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Left Panel - List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 'calc(100vh - 250px)', overflow: 'auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6">Animations</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleNew}
                size="small"
              >
                Nouveau
              </Button>
            </Box>
            <Box display="flex" alignItems="center" mb={1}>
              <Switch
                size="small"
                checked={showDescriptions}
                onChange={(e) => setShowDescriptions(e.target.checked)}
              />
              <Typography variant="caption" color="text.secondary">
                Descriptions
              </Typography>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {currentContainer !== null && (
                      <ParentDropZone
                        parentContainer={null}
                        onDrop={() => {}}
                        onClick={() => handleGoToParent()}
                      />
                    )}
                    {containers.map((container) => (
                      <SortableContainer
                        key={`container-${container.id}`}
                        container={container}
                        onNavigate={handleNavigateToContainer}
                        onEdit={handleEditContainer}
                        onDelete={handleDeleteContainer}
                        activeItemType={activeItemType}
                        isNestTarget={nestTargetId === container.id}
                        showDescription={showDescriptions}
                      />
                    ))}
                    {animations.map((animation) => (
                      <SortableAnimation
                        key={`animation-${animation.id}`}
                        animation={animation}
                        isSelected={activeAnimationId === animation.id}
                        onSelect={handleSelectAnimation}
                        onDuplicate={handleDuplicate}
                        onDelete={handleDelete}
                      />
                    ))}
                  </Box>
                </SortableContext>
              </DndContext>
            )}
          </Paper>
        </Grid>

        {/* Right Panel - Editor */}
        <Grid item xs={12} md={8}>
          {selectedAnimation ? (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Modifier l'animation</Typography>
                <Box display="flex" gap={1}>
                  <Button
                    variant={showPreview ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={<PlayIcon />}
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    {showPreview ? 'Masquer aperçu' : 'Aperçu'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FullscreenIcon />}
                    onClick={handleFullscreen}
                    disabled={!htmlContent.trim()}
                  >
                    Plein écran
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                  >
                    Sauvegarder
                  </Button>
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Titre"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                margin="normal"
                size="small"
              />

              {/* Preview */}
              {showPreview && htmlContent.trim() && (
                <Paper
                  variant="outlined"
                  sx={{ mb: 2, mt: 1, overflow: 'hidden', borderRadius: 2 }}
                >
                  <Box sx={{ p: 1, bgcolor: 'grey.100', borderBottom: '1px solid', borderBottomColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      Aperçu
                    </Typography>
                  </Box>
                  <iframe
                    ref={iframeRef}
                    srcDoc={htmlContent}
                    style={{
                      width: '100%',
                      height: '500px',
                      border: 'none',
                    }}
                    sandbox="allow-scripts allow-same-origin"
                    title="Animation Preview"
                  />
                </Paper>
              )}

              {/* HTML Editor */}
              <Box sx={{ mt: 1 }}>
                <Box sx={{ p: 1, bgcolor: 'grey.900', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CodeIcon sx={{ color: 'grey.400', fontSize: 18 }} />
                  <Typography variant="caption" sx={{ color: 'grey.400' }}>
                    HTML / CSS / JavaScript
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={20}
                  value={htmlContent}
                  onChange={(e) => setHtmlContent(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    sx: {
                      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                      borderRadius: '0 0 8px 8px',
                      '& textarea': {
                        tabSize: 2,
                      },
                    },
                  }}
                />
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <AnimationIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                Sélectionnez une animation ou créez-en une nouvelle
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Create Dialog */}
      <Dialog open={animationDialogOpen} onClose={() => setAnimationDialogOpen(false)}>
        <DialogTitle>Nouveau</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <ToggleButtonGroup
              value={createType}
              exclusive
              onChange={(_, v) => v && setCreateType(v)}
              size="small"
              fullWidth
            >
              <ToggleButton value="container">
                <FolderIcon sx={{ mr: 1 }} />
                Contenant
              </ToggleButton>
              <ToggleButton value="animation">
                <AnimationIcon sx={{ mr: 1 }} />
                Nouvelle animation
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {createType === 'container' && (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="Nom du contenant"
                fullWidth
                variant="outlined"
                value={newContainerTitle}
                onChange={(e) => setNewContainerTitle(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleCreateContainer();
                }}
              />
              <TextField
                margin="dense"
                label="Description (optionnel)"
                fullWidth
                variant="outlined"
                multiline
                rows={2}
                value={newContainerDescription}
                onChange={(e) => setNewContainerDescription(e.target.value)}
              />
            </>
          )}
          {createType === 'animation' && (
            <TextField
              autoFocus
              margin="dense"
              label="Titre de l'animation"
              fullWidth
              variant="outlined"
              value={newAnimationTitle}
              onChange={(e) => setNewAnimationTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newAnimationTitle.trim()) handleCreateNewAnimation();
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAnimationDialogOpen(false);
            setNewContainerTitle('');
            setNewContainerDescription('');
            setNewAnimationTitle('');
          }}>Annuler</Button>
          {createType === 'container' && (
            <Button variant="contained" onClick={handleCreateContainer}>Créer</Button>
          )}
          {createType === 'animation' && (
            <Button variant="contained" onClick={handleCreateNewAnimation}>Créer</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Container Dialog */}
      <Dialog open={editContainerDialogOpen} onClose={() => setEditContainerDialogOpen(false)}>
        <DialogTitle>Modifier le contenant</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du contenant"
            fullWidth
            variant="outlined"
            value={editContainerTitle}
            onChange={(e) => setEditContainerTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && editContainerTitle.trim()) handleUpdateContainer();
            }}
          />
          <TextField
            margin="dense"
            label="Description (optionnel)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={editContainerDescription}
            onChange={(e) => setEditContainerDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditContainerDialogOpen(false);
            setEditingContainer(null);
            setEditContainerTitle('');
            setEditContainerDescription('');
          }}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateContainer}>Modifier</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Animations;
